import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  fetchProvisionedInstanceIds,
  fetchAccessibleInstanceIds,
  resolveInstance,
  type ResolveInstanceResponse,
} from "../common/App.service";
import {
  setPermissions,
  setCapabilities,
  setIsFullAccess,
  setResolvedRole,
  setIsSkypointAdmin,
} from "../store/slices/authSlice";
import { logger } from "../logger-init";
import { ROUTES } from "../components/shared/constant";
import { authService } from "../utils/authService";

interface UseAppInitializationProps {
  currentUserId: string | undefined;
  getQueryString: () => string;
}

/**
 * Work already in flight for an instance, threaded from `hasAccessToInstance`
 * down to `loadInitialAppData` so the boot path never repeats a round trip:
 * the access resolution it has already awaited, and the provisioned-instance
 * lookup it started in parallel with that resolution.
 */
interface InstanceBootContext {
  resolution?: ResolveInstanceResponse;
  /** Resolves to null when the lookup failed (caller proceeds, as before). */
  provisionedIds?: Promise<string[] | null>;
}

interface UseAppInitializationReturn {
  isAppLoaded: boolean;
  isCheckResourceFailed: boolean;
  isShowCreateTenant: boolean;
  createOwnInfrastructure: boolean;
  showExternalTenantCreationError: boolean;
  instanceId: string;
  userAccesstoInstance: boolean;
  accessDeniedReason: string | null;
  hideNewdashboard: boolean;
  setInstanceId: (id: string) => void;
  checkIfResourceExist: (urlInstanceId?: string, ctx?: InstanceBootContext) => void;
  hasAccessToInstance: (instanceId: string) => void;
  loadInitialAppData: (instanceId: string, resolution?: ResolveInstanceResponse) => void;
  getUserAccess: (options?: { shouldSetAppLoaded?: boolean }) => Promise<boolean>;
  hideExternalTenantCreationError: () => void;
  setIsShowCreateTenant: (value: boolean) => void;
  setCreateOwnInfrastructure: (value: boolean) => void;
  setShowExternalTenantCreationError: (value: boolean) => void;
  setIsCheckResourceFailed: (value: boolean) => void;
  setUserAccessToInstance: (value: boolean) => void;
}

export const useAppInitialization = ({
  currentUserId,
  getQueryString,
}: UseAppInitializationProps): UseAppInitializationReturn => {
  const dispatch = useDispatch();

  const [isAppLoaded, setIsAppLoaded] = useState(false);
  const [isCheckResourceFailed, setIsCheckResourceFailed] = useState(true);
  const [isShowCreateTenant, setIsShowCreateTenant] = useState(false);
  const [createOwnInfrastructure, setCreateOwnInfrastructure] = useState(false);
  const [showExternalTenantCreationError, setShowExternalTenantCreationError] = useState(false);
  const [instanceId, setInstanceId] = useState("");
  const [userAccesstoInstance, setUserAccessToInstance] = useState(true);
  const [accessDeniedReason, setAccessDeniedReason] = useState<string | null>(null);
  const [hideNewdashboard, setHideNewdashboard] = useState(false);

  /** Push a resolve-instance result into Redux (the single place roles → permissions). */
  const applyResolution = useCallback(
    (resolution: ResolveInstanceResponse) => {
      if (resolution.hasAccess) {
        const isAdmin =
          resolution.role === "skysupport_admin" || resolution.role === "skypoint_admin";
        const isManager = resolution.role === "skysupport_manager";
        dispatch(
          setPermissions({
            isPlatformAdmin: isAdmin,
            isTenantAdmin: isAdmin || isManager,
            isInstanceAdmin: isAdmin || isManager,
          }),
        );
        dispatch(
          setCapabilities({
            allowView: true,
            allowEdit: isAdmin || isManager,
            allowDelete: isAdmin,
          }),
        );
        dispatch(setIsFullAccess(isAdmin));
        dispatch(setResolvedRole(resolution.role));
        dispatch(setIsSkypointAdmin(resolution.isSkypointAdmin === true));
        return;
      }

      // User has no access — deny
      dispatch(setResolvedRole(resolution.role || "skysupport_user"));
      dispatch(setIsSkypointAdmin(false));
      dispatch(
        setPermissions({
          isPlatformAdmin: false,
          isTenantAdmin: false,
          isInstanceAdmin: false,
        }),
      );
      dispatch(
        setCapabilities({
          allowView: true,
          allowEdit: false,
          allowDelete: false,
        }),
      );
    },
    [dispatch],
  );

  /** Deny elevated access — used when there is no instance or resolution failed. */
  const applyDefaultAccess = useCallback(() => {
    dispatch(setResolvedRole(null));
    dispatch(setIsSkypointAdmin(false));
    dispatch(
      setPermissions({
        isPlatformAdmin: false,
        isTenantAdmin: false,
        isInstanceAdmin: false,
      }),
    );
    dispatch(
      setCapabilities({
        allowView: true,
        allowEdit: false,
        allowDelete: false,
      }),
    );
  }, [dispatch]);

  const getUserAccess = useCallback(
    (options?: {
      shouldSetAppLoaded?: boolean;
      instanceId?: string;
      resolution?: ResolveInstanceResponse;
    }): Promise<boolean> => {
      const { shouldSetAppLoaded = true, instanceId: targetInstance, resolution } = options || {};

      // Resolve access via RBAC — single source of truth. A resolution the caller
      // already holds (from hasAccessToInstance) is applied without another round trip.
      const resolvedInstanceId = targetInstance || instanceId;
      if (resolvedInstanceId) {
        const pending = resolution
          ? Promise.resolve(resolution)
          : resolveInstance(resolvedInstanceId);
        return pending
          .then((result) => {
            applyResolution(result);
            if (shouldSetAppLoaded) {
              setIsAppLoaded(true);
            }
            return true;
          })
          .catch((error) => {
            logger.warn("resolve-instance failed, denying elevated access", {
              component: "useAppInitialization",
              action: "getUserAccess",
              error: error instanceof Error ? error.message : String(error),
            });
            // Deny elevated access on error — do not grant admin by default
            applyDefaultAccess();
            if (shouldSetAppLoaded) setIsAppLoaded(true);
            return true;
          });
      }

      // No instance ID — grant default access so the app loads
      applyDefaultAccess();
      if (shouldSetAppLoaded) setIsAppLoaded(true);
      return Promise.resolve(true);
    },
    [instanceId, applyResolution, applyDefaultAccess],
  );

  const loadInitialAppData = useCallback(
    (targetInstanceId: string, resolution?: ResolveInstanceResponse) => {
      getUserAccess({ shouldSetAppLoaded: false, instanceId: targetInstanceId, resolution })
        .then(() => {
          setIsAppLoaded(true);
        })
        .catch((error) => {
          logger.error("Critical initialization failed, showing app anyway", error, {
            component: "useAppInitialization",
            action: "loadInitialAppData",
          });
          setIsAppLoaded(true);
        });
    },
    [getUserAccess],
  );

  /**
   * Redirect the browser to a given instance ID, preserving the current pathname.
   */
  const redirectToInstanceUrl = useCallback((targetId: string) => {
    authService.saveInstanceId(targetId);
    const arr = window.location.href.split("/");
    const pathname =
      window.location.pathname === ROUTES.CALLBACK ? ROUTES.HOME : window.location.pathname;
    const newUrl = `${arr[0]}//${arr[2]}${pathname}?instanceid=${targetId}`;
    window.location.href = newUrl;
  }, []);

  /**
   * Start the provisioned-instance lookup without letting a failure escape as an
   * unhandled rejection (the caller may never await it if access is denied).
   * Null means "lookup failed"; ensureProvisionedOrRedirect proceeds in that case.
   */
  const startProvisionedLookup = useCallback((): Promise<string[] | null> => {
    return fetchProvisionedInstanceIds().catch((error) => {
      logger.error("Failed to check provisioned status, proceeding anyway", error, {
        component: "useAppInitialization",
        action: "ensureProvisionedOrRedirect",
      });
      return null;
    });
  }, []);

  /**
   * Verify that an instance is provisioned (has a physical schema in our DB).
   * If not, redirect to the first provisioned instance.
   * Returns true if the instance is provisioned and the caller should proceed,
   * or false if a redirect was triggered and the caller should stop.
   */
  const ensureProvisionedOrRedirect = useCallback(
    async (
      targetInstanceId: string,
      provisionedIds: Promise<string[] | null> = startProvisionedLookup(),
    ): Promise<boolean> => {
      const ids = await provisionedIds;
      // If the check failed, proceed anyway to avoid blocking the user
      if (ids === null) return true;
      if (ids.includes(targetInstanceId)) {
        return true;
      }

      logger.warn("Instance is not provisioned, redirecting to a provisioned instance", {
        component: "useAppInitialization",
        action: "ensureProvisionedOrRedirect",
        instanceId: targetInstanceId,
      });

      // Find an alternative provisioned instance
      if (ids.length > 0) {
        authService.clearInstanceSelection();
        redirectToInstanceUrl(ids[0]);
        return false;
      }

      // No provisioned instances at all — let the app proceed normally
      // (it will hit the "create tenant" flow)
      logger.warn("No provisioned instances available", {
        component: "useAppInitialization",
        action: "ensureProvisionedOrRedirect",
      });
      return true;
    },
    [redirectToInstanceUrl, startProvisionedLookup],
  );

  const redirectToPage = useCallback(
    (urlInstanceId: string, resolution?: ResolveInstanceResponse) => {
      const targetInstanceId = urlInstanceId || instanceId;
      if (isCheckResourceFailed) {
        loadInitialAppData(targetInstanceId, resolution);
      } else {
        setIsCheckResourceFailed(true);
        setTimeout(() => {
          loadInitialAppData(targetInstanceId, resolution);
        }, 5000);
      }
    },
    [instanceId, isCheckResourceFailed, loadInitialAppData],
  );

  const checkIfResourceExist = useCallback(
    async (urlInstanceId?: string, ctx?: InstanceBootContext) => {
      if (!urlInstanceId) {
        // Session should always provide an instanceId now.
        // If we still end up here, just load the app with no instance context.
        logger.warn("No instance ID available, loading app without instance", {
          component: "useAppInitialization",
          action: "checkIfResourceExist",
        });
        setIsAppLoaded(true);
        return;
      }
      const isProvisioned = await ensureProvisionedOrRedirect(urlInstanceId, ctx?.provisionedIds);
      if (isProvisioned) {
        redirectToPage(urlInstanceId, ctx?.resolution);
      }
    },
    [redirectToPage, ensureProvisionedOrRedirect],
  );

  const hasAccessToInstance = useCallback(
    (targetInstanceId: string) => {
      if (!currentUserId) {
        setUserAccessToInstance(false);
        return;
      }

      // The provisioned lookup does not depend on the access result, so it runs
      // alongside resolve-instance instead of after it. Its result and the
      // resolution are handed down the chain so neither is fetched twice.
      const provisionedIds = startProvisionedLookup();

      // Use resolve-instance to check access (replaces validateinstance-studio)
      resolveInstance(targetInstanceId).then(
        (resolution) => {
          if (resolution.hasAccess) {
            setAccessDeniedReason(null);
            checkIfResourceExist(targetInstanceId, { resolution, provisionedIds });
          } else {
            // Capture specific denial reason (suspended, inactive, etc.)
            const reason = resolution.reason || null;
            if (reason === "suspended" || reason === "inactive") {
              // Show dedicated suspended/inactive page — don't redirect
              setAccessDeniedReason(reason);
              setUserAccessToInstance(false);
              return;
            }

            authService.clearInstanceSelection();
            logger.warn("Instance inaccessible, redirecting to accessible instance", {
              component: "useAppInitialization",
              action: "hasAccessToInstance",
              instanceId: targetInstanceId,
              reason,
            });
            setAccessDeniedReason(reason);
            // Try to find any accessible instance instead
            fetchAccessibleInstanceIds()
              .then((ids) => {
                if (ids.length > 0) {
                  redirectToInstanceUrl(ids[0]);
                } else {
                  // No accessible instances — AppLoadingStates will show NoAccessCard
                  setUserAccessToInstance(false);
                }
              })
              .catch(() => {
                setUserAccessToInstance(false);
              });
          }
        },
        (error) => {
          logger.error("Failed to resolve instance access, denying access (fail-closed)", error, {
            component: "useAppInitialization",
            action: "hasAccessToInstance",
            instanceId: targetInstanceId,
          });
          // Fail closed — do NOT let the user through on resolution failure
          authService.clearInstanceSelection();
          fetchAccessibleInstanceIds()
            .then((ids) => {
              if (ids.length > 0) {
                redirectToInstanceUrl(ids[0]);
              } else {
                setUserAccessToInstance(false);
              }
            })
            .catch(() => {
              setUserAccessToInstance(false);
            });
        },
      );
    },
    [currentUserId, checkIfResourceExist, redirectToInstanceUrl, startProvisionedLookup],
  );

  const hideExternalTenantCreationErrorFn = useCallback(() => {
    setShowExternalTenantCreationError(false);
    checkIfResourceExist(instanceId);
  }, [instanceId, checkIfResourceExist]);

  return {
    isAppLoaded,
    isCheckResourceFailed,
    isShowCreateTenant,
    createOwnInfrastructure,
    showExternalTenantCreationError,
    instanceId,
    userAccesstoInstance,
    accessDeniedReason,
    hideNewdashboard,
    setInstanceId,
    checkIfResourceExist,
    hasAccessToInstance,
    loadInitialAppData,
    getUserAccess,
    hideExternalTenantCreationError: hideExternalTenantCreationErrorFn,
    setIsShowCreateTenant,
    setCreateOwnInfrastructure,
    setShowExternalTenantCreationError,
    setIsCheckResourceFailed,
    setUserAccessToInstance,
  };
};
